import { ArrowLeft, Loader2, Plus, Shield } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { API_CONFIG } from '../../config/api';
import { MobileInput } from '../common/MobileInput';
import { ui } from '../../styles/ui';

export const AddOfficerPage = () => {
  const navigate = useNavigate();
  const { apiCall, setUsers, setDataLoaded, authToken } = useAppState();
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', mobile: '', role: 'Field Officer' });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError('');
  };

  const handleBack = () => navigate('/admin/users');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError('');

    try {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile) {
        throw new Error('Please fill in all required fields');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }
      if (formData.mobile.length !== 10) {
        throw new Error('Please enter a valid 10-digit mobile number');
      }

      await apiCall(API_CONFIG.endpoints.registerEmployee, 'POST', { ...formData, mobile: `+91${formData.mobile}` }, true);

      const usersResponse = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.users}?&page=1&limit=10000`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const usersData = await usersResponse.json();
      if (usersResponse.ok && usersData.success) {
        setUsers(usersData.users || []);
        setDataLoaded((prev) => ({ ...prev, users: true }));
      }

      handleBack();
    } catch (err) {
      setSubmitError(err.message || 'Failed to register officer');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </button>

      <div className={`${ui.card} p-5`}>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-violet-600">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Add New Officer</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Register a new field officer to your company</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={ui.label}>
                First Name <span className="text-red-500">*</span>
              </label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={ui.input} placeholder="Enter first name" required disabled={submitLoading} />
            </div>
            <div>
              <label className={ui.label}>
                Last Name <span className="text-red-500">*</span>
              </label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={ui.input} placeholder="Enter last name" required disabled={submitLoading} />
            </div>
          </div>

          <div>
            <label className={ui.label}>
              Email Address <span className="text-red-500">*</span>
            </label>
            <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={ui.input} placeholder="officer@medicineadmin.com" required disabled={submitLoading} />
          </div>

          <div>
            <label className={ui.label}>
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <MobileInput
              value={formData.mobile}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, mobile: v }));
                if (submitError) setSubmitError('');
              }}
              disabled={submitLoading}
            />
          </div>

          <div>
            <label className={ui.label}>
              Role <span className="text-red-500">*</span>
            </label>
            <select name="role" value={formData.role} onChange={handleInputChange} className={ui.input} required disabled={submitLoading}>
              <option value="Field Officer">Field Officer</option>
              <option value="Dealer">Dealer</option>
            </select>
          </div>

          {submitError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300">
              {submitError}
            </div>
          )}

          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              The officer will receive login credentials via email after registration.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 sm:justify-end pt-2">
            <button type="button" onClick={handleBack} disabled={submitLoading} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100">
              Cancel
            </button>
            <button type="submit" disabled={submitLoading} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2">
              {submitLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Registering Officer...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Register Officer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
